/**
 * API Keys Service - Generate, validate, and manage API keys
 * Supports role-based scoping and quota enforcement
 */

import type { APIKey, APIKeyPermission, APIKeyUsageStats, AuthLevel } from "@/types";

class APIKeysService {
  private keys: Map<string, APIKey> = new Map();
  private usageHistory: Map<string, APIKeyUsageStats[]> = new Map();

  /**
   * Generate a new API key
   */
  generateKey(options: {
    name: string;
    permissions: APIKeyPermission[];
    userId?: string;
    expiresIn?: number; // days, undefined = no expiration
    rateLimit?: number; // requests per minute
  }): { key: string; keyData: APIKey } {
    const { name, permissions, userId, expiresIn, rateLimit = 60 } = options;

    // Generate a secure random key
    const rawKey = this.generateSecureKey();
    const prefix = rawKey.slice(0, 8);

    const keyData: APIKey = {
      id: `key_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      key: rawKey,
      prefix,
      name,
      permissions,
      rateLimit,
      createdBy: userId,
      createdAt: new Date(),
      expiresAt: expiresIn ? new Date(Date.now() + expiresIn * 24 * 60 * 60 * 1000) : undefined,
      status: "active",
      usageStats: this.createEmptyStats(),
    };

    this.keys.set(rawKey, keyData);
    this.usageHistory.set(rawKey, [keyData.usageStats]);

    return { key: rawKey, keyData };
  }

  /**
   * Validate an API key
   */
  validateKey(key: string): { valid: boolean; keyData?: APIKey; error?: string } {
    const keyData = this.keys.get(key);

    if (!keyData) {
      return { valid: false, error: "Invalid API key" };
    }

    if (keyData.status === "revoked") {
      return { valid: false, error: "API key has been revoked" };
    }

    if (keyData.status === "expired") {
      return { valid: false, error: "API key has expired" };
    }

    if (keyData.expiresAt && new Date() > keyData.expiresAt) {
      keyData.status = "expired";
      return { valid: false, error: "API key has expired" };
    }

    return { valid: true, keyData };
  }

  /**
   * Check if a key has permission for a specific action
   */
  hasPermission(keyData: APIKey, permission: APIKeyPermission): boolean {
    return keyData.permissions.includes(permission);
  }

  /**
   * Check if a key has admin permissions
   */
  isAdmin(keyData: APIKey): boolean {
    return keyData.permissions.includes("admin");
  }

  /**
   * Record API key usage
   */
  recordUsage(key: string, success: boolean, latency: number): void {
    const keyData = this.keys.get(key);
    if (!keyData) return;

    keyData.lastUsed = new Date();
    keyData.usageStats.totalRequests++;
    
    if (success) {
      keyData.usageStats.successCount++;
    } else {
      keyData.usageStats.errorCount++;
    }

    // Update average latency
    const totalLatency = keyData.usageStats.avgLatency * (keyData.usageStats.successCount - 1) + latency;
    keyData.usageStats.avgLatency = success ? totalLatency / keyData.usageStats.successCount : keyData.usageStats.avgLatency;

    // Update daily usage
    const today = new Date().toISOString().split("T")[0];
    const dailyEntry = keyData.usageStats.dailyUsage.find(d => d.date === today);
    
    if (dailyEntry) {
      dailyEntry.requests++;
    } else {
      keyData.usageStats.dailyUsage.unshift({ date: today, requests: 1 });
      // Keep only last 30 days
      if (keyData.usageStats.dailyUsage.length > 30) {
        keyData.usageStats.dailyUsage.pop();
      }
    }
  }

  /**
   * Get all API keys (for admin)
   */
  getAllKeys(): APIKey[] {
    return Array.from(this.keys.values());
  }

  /**
   * Get API keys for a specific user
   */
  getUserKeys(userId: string): APIKey[] {
    return Array.from(this.keys.values()).filter((k) => k.createdBy === userId);
  }

  /**
   * Get a key by ID
   */
  getKeyById(keyId: string): APIKey | undefined {
    return Array.from(this.keys.values()).find((k) => k.id === keyId);
  }

  /**
   * Get usage statistics for a key
   */
  getKeyUsageStats(keyId: string): APIKeyUsageStats[] {
    const keyData = Array.from(this.keys.values()).find((k) => k.id === keyId);
    if (!keyData) return [];

    return this.usageHistory.get(keyData.key) || [];
  }

  /**
   * Revoke an API key
   */
  revokeKey(keyId: string): boolean {
    for (const data of this.keys.values()) {
      if (data.id === keyId) {
        data.status = "revoked";
        return true;
      }
    }
    return false;
  }

  /**
   * Delete an API key completely
   */
  deleteKey(keyId: string): boolean {
    for (const [key, data] of this.keys.entries()) {
      if (data.id === keyId) {
        this.keys.delete(key);
        this.usageHistory.delete(key);
        return true;
      }
    }
    return false;
  }

  /**
   * Update API key permissions
   */
  updateKeyPermissions(keyId: string, permissions: APIKeyPermission[]): boolean {
    for (const data of this.keys.values()) {
      if (data.id === keyId) {
        data.permissions = permissions;
        return true;
      }
    }
    return false;
  }

  /**
   * Get total usage for today (quota checking)
   */
  getTodayUsage(userId?: string): number {
    const today = new Date().toISOString().split("T")[0];
    let total = 0;

    for (const data of this.keys.values()) {
      if (
        data.status !== "active" ||
        (userId && data.createdBy !== userId)
      ) {
        continue;
      }

      const todayStats = data.usageStats.dailyUsage.find((d) => d.date === today);
      if (todayStats) {
        total += todayStats.requests;
      }
    }

    return total;
  }

  /**
   * Get rate limit for a key
   */
  getRateLimit(key: string): number {
    const keyData = this.keys.get(key);
    return keyData?.rateLimit ?? 60;
  }

  /**
   * Create empty usage stats
   */
  private createEmptyStats(): APIKeyUsageStats {
    return {
      totalRequests: 0,
      successCount: 0,
      errorCount: 0,
      avgLatency: 0,
      lastUsed: new Date(),
      dailyUsage: [],
    };
  }

  /**
   * Generate a secure random key
   */
  private generateSecureKey(): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "binetic_";
    const randomValues = new Uint32Array(32);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < 32; i++) {
      result += chars[randomValues[i] % chars.length];
    }

    return result;
  }
}

// Export singleton instance
export const apiKeysService = new APIKeysService();
